import { useState, type FormEvent } from 'react';
import type React from 'react';
import { useAuth } from '@helios/auth-firebase-web';
import { BrandedLoginShell, type Branding } from '@helios/ui';
import { RefreshSmilesLoginLeft } from '../clients/refresh-smiles/LoginLeft';
import { KonceptSkinLoginLeft } from '../clients/koncept-skin/LoginLeft';
import { HubIntelishealthLoginLeft } from '../clients/hub-intelishealth/LoginLeft';
import { IntelishealthLoginLeft } from '../clients/intelishealth/LoginLeft';
import { DrShanmugamLoginLeft } from '../clients/drshanmugam/LoginLeft';
import { DrYoganandanLoginLeft } from '../clients/dryoganandan/LoginLeft';

/**
 * Build branding object from environment variables.
 */
export function getBranding(): Branding {
  return {
    clientId: import.meta.env.VITE_CLIENT_ID || 'default',
    orgName: import.meta.env.VITE_ORG_NAME || 'Organization',
    orgLogoUrl:
      import.meta.env.VITE_ORG_LOGO_URL ||
      `/clients/${import.meta.env.VITE_CLIENT_ID || 'default'}/logo.svg`,
    orgLogoHorizontalUrl: import.meta.env.VITE_ORG_LOGO_HORIZONTAL_URL || undefined,
    poweredBy: import.meta.env.VITE_POWERED_BY || undefined,
    supportEmail: import.meta.env.VITE_SUPPORT_EMAIL,
    supportPhone: import.meta.env.VITE_SUPPORT_PHONE,
    theme: {
      primaryColor: import.meta.env.VITE_PRIMARY_COLOR || '#2563eb',
      backgroundWash: import.meta.env.VITE_BACKGROUND_WASH || 'gradient',
    },
  };
}

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorText, setErrorText] = useState('');
  const { signIn, isLoading } = useAuth();

  const branding = getBranding();
  const primaryColor = branding.theme?.primaryColor || '#2563eb';

  const leftSlot =
    branding.clientId === 'refresh-smiles' ? (
      <RefreshSmilesLoginLeft branding={branding} />
    ) : branding.clientId === 'koncept-skin' ? (
      <KonceptSkinLoginLeft branding={branding} />
    ) : branding.clientId === 'hub-intelishealth' ? (
      <HubIntelishealthLoginLeft branding={branding} />
    ) : branding.clientId === 'intelishealth' ? (
      <IntelishealthLoginLeft branding={branding} />
    ) : branding.clientId === 'drshanmugam' ? (
      <DrShanmugamLoginLeft branding={branding} />
    ) : branding.clientId === 'dryoganandan' ? (
      <DrYoganandanLoginLeft branding={branding} />
    ) : undefined;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setErrorText('');

    if (!email || !password) {
      setErrorText('Please enter both email and password.');
      return;
    }

    try {
      await signIn(email, password);
      // App.tsx will handle navigation and access checks
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'An unexpected error occurred. Please try again.';
      setErrorText(message);
    }
  };

  return (
    <BrandedLoginShell
      branding={branding}
      errorText={errorText}
      isLoading={isLoading}
      leftSlot={leftSlot}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Email Field */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1.5">
            Email address
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="block w-full px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400 bg-white border border-slate-200 rounded-xl ring-1 ring-transparent focus:outline-none focus:border-transparent focus:ring-2 transition-shadow disabled:opacity-50 disabled:bg-slate-50"
            style={
              {
                '--tw-ring-color': primaryColor,
              } as React.CSSProperties
            }
            onFocus={(e) => {
              e.target.style.setProperty('--tw-ring-color', primaryColor);
            }}
            placeholder="you@example.com"
            disabled={isLoading}
          />
        </div>

        {/* Password Field */}
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1.5">
            Password
          </label>
          <input
            id="password"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="block w-full px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400 bg-white border border-slate-200 rounded-xl ring-1 ring-transparent focus:outline-none focus:border-transparent focus:ring-2 transition-shadow disabled:opacity-50 disabled:bg-slate-50"
            style={
              {
                '--tw-ring-color': primaryColor,
              } as React.CSSProperties
            }
            onFocus={(e) => {
              e.target.style.setProperty('--tw-ring-color', primaryColor);
            }}
            placeholder="Enter your password"
            disabled={isLoading}
          />
        </div>

        {/* Forgot Password Link */}
        <div className="flex items-center justify-end">
          <button
            type="button"
            className="text-sm font-medium hover:underline"
            style={{ color: primaryColor }}
            onClick={() => {
              alert('Forgot password functionality coming soon.');
            }}
          >
            Forgot password?
          </button>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full flex justify-center py-2.5 px-4 text-sm font-semibold text-white rounded-xl shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          style={
            {
              backgroundColor: primaryColor,
              '--tw-ring-color': primaryColor,
            } as React.CSSProperties
          }
          onMouseEnter={(e) => {
            if (!isLoading) {
              e.currentTarget.style.filter = 'brightness(0.9)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.filter = 'brightness(1)';
          }}
        >
          {isLoading ? 'Signing in...' : 'Sign In'}
        </button>
      </form>
    </BrandedLoginShell>
  );
}
